/**
 * OrderHoverTooltip — the Trioty floating hover preview. Follows the cursor
 * (fixed, offset 16/16) and shows the order code, its status badge, and the
 * compact 4-step timeline stepper. Rendered once per list (table / card grid);
 * the list tracks which order is hovered and the cursor position.
 */

import { createPortal } from "react-dom";
import type { SellerOrderListItem } from "@/lib/api/types";
import { useLocale } from "@/lib/i18n/intl";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { OrderStatusBadge } from "./order-visuals";
import { OrderStepper } from "./OrderStepper";

export type HoverTip = { order: SellerOrderListItem; x: number; y: number };

export function OrderHoverTooltip({ tip }: { tip: HoverTip | null }) {
  const locale = useLocale();
  if (!tip || typeof document === "undefined") return null;
  const { order, x, y } = tip;
  return createPortal(
    <div
      role="tooltip"
      style={{ position: "fixed", left: x, top: y, transform: "translate(16px, 16px)", zIndex: 95, pointerEvents: "none", width: 236 }}
      className="rounded-[13px] border border-[color:var(--card-br)] bg-[color:var(--card)] px-[14px] pb-1.5 pt-[13px] shadow-[0_16px_40px_rgba(0,0,0,0.22)]"
    >
      <div className="mb-3 flex items-center justify-between gap-2.5">
        <span className="font-mono text-[12.5px] font-bold text-[color:var(--text-primary)]">
          #{order.orderCode}
        </span>
        <OrderStatusBadge status={order.status} />
      </div>
      <OrderStepper status={order.status} placedSub={relativeAgo(order.createdAt, locale) ?? ""} compact />
    </div>,
    document.body,
  );
}
